import { useQuery } from "@tanstack/react-query";

export interface ScoreTrendRow {
  snapshotDate: string;
  scoreCode: string;
  scoreBand: string | null;
  interactionCount: number;
  animalDistinctCount: number;
}

export interface ScoreTrendResult {
  scope: "system" | "location";
  locationCode: string | null;
  days: number;
  rows: ScoreTrendRow[];
}

export interface TopScoreRow {
  scoreCode: string;
  scoreBand: string | null;
  totalInteractions: number;
  totalAnimals: number;
  percentOfTotal: number;
}

export interface TopScoresResult {
  scope: "system" | "location";
  locationCode: string | null;
  days: number;
  totalInteractions: number;
  scores: TopScoreRow[];
}

export interface LocationTrendPoint {
  date: string;
  locations: Record<string, { interactions: number; animals: number; redPct: number }>;
}

export interface LocationTrendsResult {
  days: number;
  series: LocationTrendPoint[];
  locations: string[];
}

export interface AlertAnimal {
  animalId: string;
  name: string;
  species: string;
  location: string;
  streakDays?: number;
  spikeRate?: number;
  severityCount?: number;
}

export interface AlertsSummaryResult {
  totalTracked: number;
  strugglingNow: { count: number; animals: AlertAnimal[] };
  streakFlagged: { count: number; animals: AlertAnimal[] };
  dailySpikeFlagged: { count: number; animals: AlertAnimal[] };
  trendDistribution: { worsening: number; stable: number; improving: number; insufficient: number };
}

export function useBehaviorScoreTrend(
  days: number,
  scope: "system" | "location",
  locationCode?: string
) {
  const queryParams = new URLSearchParams({
    days: String(days),
    scope,
    ...(locationCode && scope === "location" ? { location: locationCode } : {}),
  });

  return useQuery<ScoreTrendResult>({
    queryKey: [`/api/behavior/analytics/scores?${queryParams.toString()}`],
    enabled: scope === "system" || !!locationCode,
  });
}

export function useBehaviorTopScores(
  days: number,
  scope: "system" | "location",
  locationCode?: string
) {
  const queryParams = new URLSearchParams({
    days: String(days),
    scope,
    ...(locationCode && scope === "location" ? { location: locationCode } : {}),
  });

  return useQuery<TopScoresResult>({
    queryKey: [`/api/behavior/analytics/top-scores?${queryParams.toString()}`],
    enabled: scope === "system" || !!locationCode,
  });
}

export function useBehaviorAnalyticsLocations() {
  return useQuery<{ locations: string[] }>({
    queryKey: ["/api/behavior/analytics/locations"],
  });
}

export function useLocationTrends(days: number) {
  const queryParams = new URLSearchParams({ days: String(days) });
  return useQuery<LocationTrendsResult>({
    queryKey: [`/api/behavior/analytics/location-trends?${queryParams.toString()}`],
  });
}

export function useAlertsSummary(location?: string, species?: string) {
  const params: Record<string, string> = {};
  if (location) params.location = location;
  if (species) params.species = species;
  const queryParams = new URLSearchParams(params);
  return useQuery<AlertsSummaryResult>({
    queryKey: [`/api/behavior/analytics/alerts-summary?${queryParams.toString()}`],
  });
}

export interface SilentAnimalsResult {
  days: number;
  totalInCare: number;
  noRecentInteractions: number;
  pct: number;
}

export interface CoverageTimePoint {
  date: string;
  inCareCount: number;
  silentCount: number;
  silentPct: number;
}

export interface CoverageTrendResult {
  days: number;
  windowDays: number;
  series: CoverageTimePoint[];
}

export interface SilentAnimalRow {
  animalId: string;
  name: string;
  species: string;
  location: string;
  lastInteractionDate: string | null;
  daysSinceLast: number | null;
}

export function useSilentAnimalList(scope: "system" | "location", locationCode?: string, windowDays = 14, species?: string) {
  const queryParams = new URLSearchParams({
    scope,
    window: String(windowDays),
    ...(scope === "location" && locationCode ? { location: locationCode } : {}),
    ...(species ? { species } : {}),
  });
  return useQuery<SilentAnimalRow[]>({
    queryKey: [`/api/behavior/analytics/silent-animal-list?${queryParams.toString()}`],
  });
}

export function useAssessedAnimalList(scope: "system" | "location", locationCode?: string, windowDays = 14, species?: string) {
  const queryParams = new URLSearchParams({
    scope,
    window: String(windowDays),
    ...(scope === "location" && locationCode ? { location: locationCode } : {}),
    ...(species ? { species } : {}),
  });
  return useQuery<SilentAnimalRow[]>({
    queryKey: [`/api/behavior/analytics/assessed-animal-list?${queryParams.toString()}`],
  });
}

export function useCoverageTrend(days: number, scope: "system" | "location", locationCode?: string, windowDays = 14, species?: string) {
  const queryParams = new URLSearchParams({
    days: String(days),
    scope,
    window: String(windowDays),
    ...(scope === "location" && locationCode ? { location: locationCode } : {}),
    ...(species ? { species } : {}),
  });
  return useQuery<CoverageTrendResult>({
    queryKey: [`/api/behavior/analytics/coverage-trend?${queryParams.toString()}`],
  });
}

export function useSilentAnimals(days: number, scope: "system" | "location", locationCode?: string, windowDays = 14, species?: string) {
  const queryParams = new URLSearchParams({
    days: String(days),
    scope,
    window: String(windowDays),
    ...(scope === "location" && locationCode ? { location: locationCode } : {}),
    ...(species ? { species } : {}),
  });
  return useQuery<SilentAnimalsResult>({
    queryKey: [`/api/behavior/analytics/silent-animals?${queryParams.toString()}`],
  });
}
